import { SkeletonData, Spine } from "@pixi-spine/all-4.0";
import * as PIXI from "pixi.js";
import { Dictionary } from "../../util/collection/Dictionary";
import { Logger } from "../../util/log/Logger";
import { OrientationType } from "../renderer/constant/RendererConstants";
import { FontManager } from "./FontManager";
import { SpineManager } from "./SpineManager";
/**
 * AssetManager.ts
 */
export class AssetManager {
    constructor() {
        this.missing = [];
        if (AssetManager.instance) {
            throw new Error("AssetManager Singleton Already Initialised");
        }
        this.create();
        AssetManager.instance = this;
    }
    create() {
        this.fontManager = FontManager.getInstance();
        this.map = new Dictionary();
    }
    logMissing(type, id, spritesheet) {
        const missing = id + " : " + spritesheet;
        if (this.missing.indexOf(missing) === -1) {
            Logger.verbose("Missing " + type + " for " + missing);
            this.missing.push(missing);
        }
    }
    addPath(filePath) {
        this.map.setValue(filePath.getId() + ":" + filePath.getComponent(), filePath);
    }
    getPath(id, component) {
        return this.map.getValue(id + ":" + component);
    }
    getSound(id, component) {
        return this.map.getValue(id + ":" + component);
    }
    hasSprite(properties, orientationType) {
        let frame = this.formatFrame(properties, orientationType);
        return this.hasFrame(frame);
    }
    getSprite(properties, orientationType) {
        let frame = this.formatFrame(properties);
        let frameResponsive = this.formatFrame(properties, orientationType);
        let sprite;
        if (this.hasFrame(frame)) {
            sprite = PIXI.Sprite.from(frame);
        }
        else if (this.hasFrame(frameResponsive)) {
            sprite = PIXI.Sprite.from(frameResponsive);
        }
        if (sprite) {
            if (properties.position) {
                sprite.position.copyFrom(properties.position);
            }
            if (properties.scale) {
                sprite.scale.copyFrom(properties.scale);
            }
            if (properties.rotation) {
                sprite.rotation = properties.rotation;
            }
            if (properties.anchor) {
                sprite.anchor.copyFrom(properties.anchor);
            }
        }
        else {
            this.logMissing("Sprite", properties.id, properties.spriteSheet);
            sprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
        }
        return sprite;
    }
    getMovieClip(properties, reposition = true, orientationType) {
        let textures = [];
        let frame = this.formatFrame(properties);
        let frameResponsive = this.formatFrame(properties, orientationType);
        if (properties.frameNames && properties.frameNames.length) {
            const namedFrames = this.formatNamedFrames(properties);
            const namedFramesResponsive = this.formatNamedFrames(properties, orientationType);
            for (let i = 0; i < properties.numberOfFrames; i++) {
                if (this.hasFrame(namedFramesResponsive[i])) {
                    let texture = PIXI.Texture.from(namedFramesResponsive[i]);
                    textures.push(texture);
                }
                else if (this.hasFrame(namedFrames[i])) {
                    let texture = PIXI.Texture.from(namedFrames[i]);
                    textures.push(texture);
                }
            }
        }
        else if (properties.numberOfFrames > 1) {
            for (let i = 0; i < properties.numberOfFrames; i++) {
                const name = frame + this.formatTexture(i);
                const nameResponsive = frameResponsive + this.formatTexture(i);
                if (this.hasFrame(name)) {
                    let texture = PIXI.Texture.from(name);
                    textures.push(texture);
                }
                else if (this.hasFrame(nameResponsive)) {
                    let texture = PIXI.Texture.from(nameResponsive);
                    textures.push(texture);
                }
            }
        }
        else {
            if (this.hasFrame(frame)) {
                textures.push(PIXI.Texture.from(frame));
            }
            else if (this.hasFrame(frameResponsive)) {
                textures.push(PIXI.Texture.from(frameResponsive));
            }
        }
        let clip;
        if (textures.length > 0) {
            clip = new PIXI.AnimatedSprite(textures);
            if (properties.position && reposition) {
                clip.position.copyFrom(properties.position);
            }
            if (properties.scale) {
                clip.scale.copyFrom(properties.scale);
            }
            if (properties.rotation) {
                clip.rotation = properties.rotation;
            }
            if (properties.anchor) {
                clip.anchor.copyFrom(properties.anchor);
            }
            clip.loop = properties.loop;
        }
        if (!clip) {
            this.logMissing("MovieClip", properties.id, properties.spriteSheet);
            clip = new PIXI.AnimatedSprite([PIXI.Texture.EMPTY]);
        }
        return clip;
    }
    getSpine(properties, position = true, orientationType) {
        const oType = orientationType === OrientationType.LANDSCAPE ? "Landscape" : "Portrait";
        const id = properties.id + (orientationType ? `-${oType}` : "");
        const path = this.getPath(id, properties.spriteSheet) || this.getPath(properties.id, properties.spriteSheet);
        let spine;
        if (path) {
            let spineData = SpineManager.getInstance().get(path.getUrl()).data;
            spine = new Spine(spineData);
            if (position && properties.position) {
                spine.position.copyFrom(properties.position);
            }
            if (properties.rotation) {
                spine.rotation = properties.rotation;
            }
        }
        else {
            this.logMissing("Spine", properties.id, properties.spriteSheet);
            const skeleton = new SkeletonData();
            skeleton.version = "4.0";
            spine = new Spine(skeleton);
        }
        return spine;
    }
    formatFrame(properties, orientationType) {
        if (orientationType) {
            const oKey = orientationType === OrientationType.LANDSCAPE ? "Landscape" : "Portrait";
            return properties.spriteSheet + "-" + oKey + "-" + properties.id;
        }
        return properties.spriteSheet + "-" + properties.id;
    }
    formatNamedFrames(properties, orientationType) {
        if (orientationType) {
            const oKey = orientationType === OrientationType.LANDSCAPE ? "Landscape" : "Portrait";
            return properties?.frameNames?.map(name => properties.spriteSheet + "-" + oKey + "-" + properties.id + name) || [];
        }
        return properties?.frameNames?.map(name => properties.spriteSheet + "-" + properties.id + name) || [];
    }
    formatTexture(frame) {
        frame++;
        let r = frame.toString();
        while (r.length < 4) {
            r = "0" + r;
        }
        return r;
    }
    hasFrame(frame) {
        return PIXI.utils.TextureCache[frame] != null;
    }
    hasFrameId(id, spriteSheet) {
        const keys = Object.keys(PIXI.utils.TextureCache).filter(key => key.indexOf(spriteSheet) === 0);
        return keys.findIndex(key => {
            const fIndex = key.indexOf(id);
            return fIndex > -1 && (fIndex + id.length === key.length);
        }) > -1;
    }
    static getInstance() {
        if (AssetManager.instance === null) {
            AssetManager.instance = new AssetManager();
        }
        return AssetManager.instance;
    }
    findAssetId(url) {
        const assetIndex = this.map.getValues().findIndex((filePath) => {
            const splitUrl = filePath.getUrl().split('/');
            return splitUrl[splitUrl.length - 1] === url;
        });
        if (assetIndex > -1) {
            return this.map.getKeys()[assetIndex];
        }
        else {
            return null;
        }
    }
}
AssetManager.instance = null;
//# sourceMappingURL=AssetManager.js.map